import React, { useEffect } from "react";
import { useMaterials } from "@/contexts/MaterialsContext";
import { imageUrl } from "@/lib/api";

/**
 * Filter chips for Material. value === "all" | one of the material names.
 * Click an active chip again to clear the filter (returns to "all").
 *
 * Shows a small thumbnail dot when the material has `thumbnail_image` set,
 * matching how ColourChips renders swatch dots.
 *
 * Optional `relevantNames` prop — when provided, only materials whose name
 * is in the set are shown. If the currently-selected material drops out of
 * scope, the filter auto-resets to "all" to avoid confusing empty grids.
 *
 * Renders nothing if the visible set is empty (e.g. dept has no items with
 * any material assigned).
 */
const MaterialChips = ({ value, onChange, testidPrefix = "mat", relevantNames }) => {
  const { materials } = useMaterials();
  const visible = relevantNames
    ? materials.filter((m) => relevantNames.includes(m.name))
    : materials;

  // If the active filter is no longer in scope, clear it.
  useEffect(() => {
    if (value !== "all" && relevantNames && !relevantNames.includes(value)) {
      onChange("all");
    }
  }, [value, relevantNames, onChange]);

  if (!visible.length) return null;
  return (
    <div className="flex flex-wrap gap-2" data-testid={`${testidPrefix}-chips`}>
      {visible.map((m) => {
        const active = value === m.name;
        return (
          <button
            key={m.name}
            data-testid={`${testidPrefix}-${m.name}`}
            onClick={() => onChange(active ? "all" : m.name)}
            type="button"
            aria-pressed={active}
            className={`nb-btn px-3 py-1.5 text-sm transition-all flex items-center gap-1.5 ${
              active ? "nb-btn-dark" : "bg-white"
            }`}
          >
            {m.thumbnail_image && (
              <img
                src={imageUrl(m.thumbnail_image)}
                alt=""
                className="inline-block w-4 h-4 rounded-full border border-black/40 object-cover"
              />
            )}
            {m.name}
          </button>
        );
      })}
    </div>
  );
};

export default MaterialChips;
